import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/auth.service';

const user = authService.getCurrentUser();

export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      // First get the auth token
      const tokenResponse = await authService.login(username, password);
      
      // Then get the user details including permissions
      const userDetailsResponse = await authService.getUserDetails();
      
      return { 
        user: {
          ...tokenResponse,
          ...userDetailsResponse
        }
      };
    } catch (error) {
      // Handle error
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  authService.logout();
});

export const register = createAsyncThunk(
  'auth/register',
  async ({ username, email, password, firstName, lastName, phone }, { rejectWithValue }) => {
    try {
      await authService.register(username, email, password, firstName, lastName, phone);
      return { success: true };
    } catch (error) {
      const message = 
        (error.response && 
          error.response.data && 
          Object.values(error.response.data).join(' ')) || 
        error.message || 
        error.toString();
      return rejectWithValue(message);
    }
  }
);

export const refreshUserData = createAsyncThunk(
  'auth/refreshUserData',
  async (_, { rejectWithValue }) => {
    try {
      const userDetailsResponse = await authService.getUserDetails();
      return { userDetails: userDetailsResponse };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const initialState = user
  ? { 
      isLoggedIn: true, 
      user, 
      permissions: user.permissions || [],  // Use direct permissions instead of user.role.permissions
      isLoading: false, 
      error: null 
    }
  : { 
      isLoggedIn: false, 
      user: null,
      permissions: [],
      isLoading: false, 
      error: null 
    };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    manualRefreshUserData: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload.user
      };
      state.permissions = action.payload.user.permissions || [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.user = action.payload.user;
        state.permissions = action.payload.user.permissions || [];
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user = null;
        state.permissions = [];
        state.error = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.user = null;
        state.permissions = [];
      })
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(refreshUserData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshUserData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = {
          ...state.user,
          ...action.payload.userDetails
        };
        state.permissions = action.payload.userDetails.permissions || [];
      })
      .addCase(refreshUserData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // If token is invalid, log the user out
        if (action.payload.includes('token') || action.payload.includes('unauthorized')) {
          state.isLoggedIn = false;
          state.user = null;
          state.permissions = [];
        }
      });
  }
});

export const { clearError, manualRefreshUserData } = authSlice.actions;
export default authSlice.reducer;